import { InjectionToken, Injectable, inject } from '@angular/core';
import {
  Auth,
  AuthCredential,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  getRedirectResult,
  linkWithCredential,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  sendEmailVerification,
  reload,
  getIdToken,
  signOut,
  UserCredential,
} from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { catchError, defaultIfEmpty, from, map, Observable, Subject, throwError } from 'rxjs';
import { bindFirebaseInjectionContext } from './firebase-injection-context';
import { createAppleAuthProvider } from './apple-auth.provider';
import { isApplePopupBlocked, resolveAppleAuthMode } from './apple-auth.strategy';
import type { FirebaseError } from '@angular/fire/app';

export interface FirebaseAuthApi {
  createAppleAuthProvider: typeof createAppleAuthProvider;
  createUserWithEmailAndPassword: typeof createUserWithEmailAndPassword;
  getIdToken: typeof getIdToken;
  getRedirectResult: typeof getRedirectResult;
  GoogleAuthProvider: typeof GoogleAuthProvider;
  httpsCallable: typeof httpsCallable;
  isApplePopupBlocked: typeof isApplePopupBlocked;
  linkWithCredential: typeof linkWithCredential;
  credentialFromError: typeof OAuthProvider.credentialFromError;
  reload: typeof reload;
  resolveAppleAuthMode: typeof resolveAppleAuthMode;
  sendEmailVerification: typeof sendEmailVerification;
  signInWithEmailAndPassword: typeof signInWithEmailAndPassword;
  signInWithPopup: typeof signInWithPopup;
  signInWithRedirect: typeof signInWithRedirect;
  signOut: typeof signOut;
}

export const FIREBASE_AUTH_API = new InjectionToken<FirebaseAuthApi>('FIREBASE_AUTH_API', {
  providedIn: 'root',
  factory: () =>
    bindFirebaseInjectionContext({
      createAppleAuthProvider,
      createUserWithEmailAndPassword,
      getIdToken,
      getRedirectResult,
      GoogleAuthProvider,
      httpsCallable,
      isApplePopupBlocked,
      linkWithCredential,
      credentialFromError: OAuthProvider.credentialFromError,
      reload,
      resolveAppleAuthMode,
      sendEmailVerification,
      signInWithEmailAndPassword,
      signInWithPopup,
      signInWithRedirect,
      signOut,
    }),
});

@Injectable({
  providedIn: 'root',
})
/**
 * Service for handling Firebase authentication operations within an Angular application.
 *
 * Provides methods for user registration, sign-in, and sign-out using Firebase Auth.
 * Exposes an observable subject to notify subscribers when a sign-out action occurs.
 *
 * @remarks
 * - Utilizes Angular's dependency injection to obtain the Firebase Auth instance.
 * - All authentication methods return RxJS Observables for reactive programming.
 *
 * @example
 * ```typescript
 * // Register a new user
 * firebaseAuthService.signUp('user@example.com', 'password123').subscribe(...);
 *
 * // Sign in an existing user
 * firebaseAuthService.signIn('user@example.com', 'password123').subscribe(...);
 *
 * // Sign out the current user
 * firebaseAuthService.signOut().subscribe(...);
 *
 * // Listen for sign-out events
 * ```
 *  firebaseAuthService.signout$.subscribe(() => {  handle sign-out  });
 */
export class FirebaseAuthService {
  /**
   * Instance of the Firebase Auth service, injected via Angular's dependency injection.
   * Used to handle authentication operations such as sign-in, sign-out, and user management.
   */
  private auth: Auth | null = inject(Auth, { optional: true });
  private readonly functions = inject(Functions, { optional: true });
  private readonly authApi = inject(FIREBASE_AUTH_API);
  /**
   * Subject that emits when a sign-out action is triggered.
   * Subscribers can listen to this observable to perform cleanup or redirect logic upon user sign-out.
   */
  signout$: Subject<void> = new Subject<void>();
  private appleRedirectResult: UserCredential | null = null;
  private appleRedirectError: unknown = null;
  constructor() {}

  /**
   * Registers a new user using the provided email and password.
   *
   * @param email - The email address of the user to register.
   * @param password - The password for the new user account.
   * @returns An Observable that emits the UserCredential upon successful registration.
   */
  signUp(email: string, password: string): Promise<UserCredential> {
    if (!this.auth) {
      return Promise.reject(new Error('Firebase Auth is unavailable'));
    }

    // TODO: analytics - track signup_attempt (email)
    return this.authApi
      .createUserWithEmailAndPassword(this.auth, email, password)
      .catch((error) => {
        // Log error with contextual information
        throw error;
      });
  }

  /**
   * Signs in a user using their email and password credentials.
   *
   * @param email - The user's email address.
   * @param password - The user's password.
   * @returns An Observable that emits the UserCredential upon successful sign-in.
   */
  signIn(email: string, password: string): Observable<UserCredential> {
    if (!this.auth) {
      return throwError(() => new Error('Firebase Auth is unavailable'));
    }

    // TODO: analytics - track signin_attempt (email)
    return from(this.authApi.signInWithEmailAndPassword(this.auth, email, password));
  }

  recordLoginFailure(email: string, reasonCode: string): Promise<void> {
    if (!this.functions) {
      return Promise.reject(new Error('Firebase Functions is unavailable'));
    }

    const recordFailure = this.authApi.httpsCallable(this.functions, 'recordloginfailure');
    return recordFailure({ email, reasonCode }).then(() => undefined);
  }

  signInWithGoogle(): Promise<UserCredential> {
    if (!this.auth) {
      return Promise.reject(new Error('Firebase Auth is unavailable'));
    }

    return this.authApi.signInWithPopup(this.auth, new this.authApi.GoogleAuthProvider());
  }

  async signInWithApple(): Promise<UserCredential | null> {
    if (!this.auth) {
      throw new Error('Firebase Auth is unavailable');
    }

    const provider = this.authApi.createAppleAuthProvider();
    if (this.authApi.resolveAppleAuthMode() === 'redirect') {
      await this.authApi.signInWithRedirect(this.auth, provider);
      return null;
    }

    try {
      return await this.authApi.signInWithPopup(this.auth, provider);
    } catch (error) {
      if (!this.authApi.isApplePopupBlocked(error)) throw error;
      await this.authApi.signInWithRedirect(this.auth, provider);
      return null;
    }
  }

  async recoverAppleRedirectResult(): Promise<UserCredential | null> {
    if (!this.auth) {
      return null;
    }

    let credential: UserCredential | null;
    try {
      credential = await this.authApi.getRedirectResult(this.auth);
    } catch (error) {
      this.appleRedirectError = error;
      return null;
    }
    if (credential?.providerId !== 'apple.com') return null;
    this.appleRedirectResult = credential;
    return credential;
  }

  consumeAppleRedirectResult(): UserCredential | null {
    const result = this.appleRedirectResult;
    this.appleRedirectResult = null;
    return result;
  }

  consumeAppleRedirectError(): unknown {
    const error = this.appleRedirectError;
    this.appleRedirectError = null;
    return error;
  }

  async linkAppleCredential(error: unknown): Promise<UserCredential> {
    if (!this.auth) {
      throw new Error('Firebase Auth is unavailable');
    }

    if (!isFirebaseError(error)) {
      throw new Error('An Apple credential is required to link this account.');
    }
    const credential = this.authApi.credentialFromError(error);
    if (!credential || credential.providerId !== 'apple.com') {
      throw new Error('An Apple credential is required to link this account.');
    }
    const user = this.auth.currentUser;
    if (!user) throw new Error('Authentication is required.');
    return this.authApi.linkWithCredential(user, credential as AuthCredential);
  }

  async sendVerificationEmail(): Promise<void> {
    if (!this.auth || !this.functions) {
      throw new Error('Firebase Auth is unavailable');
    }

    const user = this.auth.currentUser;
    if (!user) throw new Error('Authentication is required.');
    const requestVerificationEmail = this.authApi.httpsCallable(
      this.functions,
      'requestverificationemail',
    );
    const result = await requestVerificationEmail();
    if (!(result.data as { allowed?: boolean }).allowed) return;
    await this.authApi.sendEmailVerification(user);
  }

  async refreshVerificationStatus(): Promise<boolean> {
    if (!this.auth) return false;

    const user = this.auth.currentUser;
    if (!user) return false;
    await this.authApi.reload(user);
    const verified = this.auth.currentUser?.emailVerified ?? false;
    if (verified) {
      if (!this.functions) return verified;
      const refreshVerificationStatus = this.authApi.httpsCallable(
        this.functions,
        'refreshverificationstatus',
      );
      await refreshVerificationStatus();
      await this.refreshCurrentSession();
    }
    return verified;
  }

  async changeEmail(newEmail: string, operationId = crypto.randomUUID()): Promise<void> {
    if (!this.functions || !this.auth) {
      throw new Error('Firebase Auth is unavailable');
    }

    const changeEmail = this.authApi.httpsCallable(this.functions, 'changeemail');
    await changeEmail({ newEmail, operationId });
    const user = this.auth.currentUser;
    if (!user) throw new Error('Authentication is required.');
    await this.authApi.reload(user);
    await this.authApi.getIdToken(user, true);
    await this.refreshCurrentSession();
    await this.sendVerificationEmail();
  }

  private async refreshCurrentSession(): Promise<void> {
    if (!this.functions) return;

    const sessionId = sessionStorage.getItem('barberiaspr.sessionId');
    if (!sessionId) return;

    const refreshSession = this.authApi.httpsCallable(this.functions, 'refreshsession');
    await refreshSession({ sessionId }).catch(() => undefined);
  }

  /**
   * Signs out the current user from Firebase authentication.
   *
   * This method returns an Observable that emits when the sign-out operation is complete.
   * Additionally, it notifies subscribers via the `signout$` subject.
   *
   * @returns {Observable<void>} An Observable that completes when the user is signed out.
   */
  signOut(): Observable<boolean> {
    if (!this.auth) {
      return throwError(() => new Error('Firebase Auth is unavailable'));
    }

    return from(this.authApi.signOut(this.auth)).pipe(
      map(() => {
        this.signout$.next();
        // TODO: analytics - track sign_out
        return true;
      }),
      defaultIfEmpty(true),
      catchError((error) => {
        // Propagate the original error so callers can handle the failure with a proper catch.
        return throwError(() => error);
      }),
    );
  }
}

function isFirebaseError(error: unknown): error is FirebaseError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'name' in error &&
    'message' in error
  );
}
